/**
 * Полоса расхода: сколько из купленного израсходовано.
 *
 * design: derived, not documented — в дизайн-системе полосы описаны для
 * лидерборда и прибора (data-viz.md §3), обе поверхности тёмные. Здесь тот же
 * механизм на светлой: `--w` живёт **в разметке**, а не в скрипте, число рядом
 * с полосой обязательно (серая заливка даёт 1,34:1 к дорожке и сама по себе
 * графиком не считается), заливка — `--accent`, перерасход — `--unseen-ink`.
 *
 * Ширина считается от лимита, а не от максимума в выборке: здесь сравнивают не
 * строки между собой, а расход с потолком. При перерасходе полоса упирается в
 * край, но число говорит правду — обрезать число до лимита нельзя, иначе
 * перерасход исчезает с экрана.
 */

import { countOf, coverageOf, percent } from '@shared/src/format';

export function Meter({
  label,
  used,
  limit,
  unit,
  hint,
}: {
  label: string;
  used: number;
  /** `null` — метрика тарифом не ограничена. Полосы тогда нет: делить не на что. */
  limit: number | null;
  unit: [one: string, few: string, many: string];
  hint?: string;
}) {
  // `null` — метрика тарифом не ограничена. Ноль — ограничена нулём, и это
  // совсем другое: полоса нужна, а делить на ноль нельзя.
  const unlimited = limit === null;
  const share =
    !unlimited && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = !unlimited && used > limit;

  return (
    <div className="meter">
      <div className="meter-top">
        <span className="meter-label">{label}</span>
        <span className="meter-val tnum">
          {unlimited ? countOf(used, ...unit) : `${coverageOf(used, limit, ...unit)}`}
        </span>
      </div>

      {unlimited ? (
        <p className="stamp">Тарифом не ограничено</p>
      ) : (
        <>
          <div
            className={`meter-track${over ? ' over' : ''}`}
            role="progressbar"
            aria-valuenow={used}
            aria-valuemin={0}
            aria-valuemax={limit}
            aria-label={label}
          >
            <i style={{ ['--w' as string]: `${share}%` }} />
          </div>
          <p className="stamp">
            {over
              ? `Израсходовано больше купленного на ${countOf(used - limit, ...unit)}`
              : `Израсходовано ${percent(share)}`}
            {hint ? ` · ${hint}` : ''}
          </p>
        </>
      )}
    </div>
  );
}
